import React from 'react';
import { useLocation } from 'react-router-dom';
import { Helmet } from '@lib/helmet-shim';
import { buildCanonical } from '@lib/url';
import { getRobotsMeta, SEO_CONFIG, siteConfig } from '../config';

interface SeoHeadProps {
  title?: string;
  description?: string;
  keywords?: string[];
  pageSlug?: string; // 'terms', 'dmca', 'privacy', etc.
  image?: string;
  /** Page path used for canonical/og:url. Defaults to the current route pathname. */
  path?: string;
  canonicalUrl?: string;
  jsonLd?: Record<string, unknown>;
  /** When true, `title` is used as the full document title (no suffix appended). */
  rawTitle?: boolean;
}

/**
 * SEO Head Component
 * Handles all meta tags including dynamic robots meta for different page types
 * Automatically applies noindex,nofollow for terms/dmca/privacy pages
 */
const SeoHead: React.FC<SeoHeadProps> = ({
  title,
  description,
  keywords,
  pageSlug,
  image = SEO_CONFIG.DEFAULT_IMAGE,
  path,
  canonicalUrl,
  jsonLd,
  rawTitle = false,
}) => {
  const location = useLocation();
  const robotsMeta = getRobotsMeta(pageSlug);
  const fullTitle = title
    ? (rawTitle ? title : `${title}${SEO_CONFIG.TITLE_SEPARATOR}${SEO_CONFIG.TITLE_SUFFIX}`)
    : undefined;
  const canonical = buildCanonical(canonicalUrl ?? path ?? location.pathname);

  return (
    <Helmet>
      {fullTitle && <title>{fullTitle}</title>}
      {description && <meta name="description" content={description} />}
      {keywords && keywords.length > 0 && <meta name="keywords" content={keywords.join(', ')} />}
      <meta name="robots" content={robotsMeta} />
      <link rel="canonical" href={canonical} />

      {/* Open Graph */}
      <meta property="og:type" content="website" />
      <meta property="og:site_name" content={siteConfig.siteName} />
      {fullTitle && <meta property="og:title" content={fullTitle} />}
      {description && <meta property="og:description" content={description} />}
      <meta property="og:image" content={image} />
      <meta property="og:locale" content={SEO_CONFIG.OG_LOCALE} />
      <meta property="og:url" content={canonical} />

      {/* Twitter Card */}
      <meta name="twitter:card" content={SEO_CONFIG.TWITTER_CARD_TYPE} />
      {fullTitle && <meta name="twitter:title" content={fullTitle} />}
      {description && <meta name="twitter:description" content={description} />}
      <meta name="twitter:image" content={image} />

      {/* JSON-LD Structured Data */}
      {jsonLd && (
        <script type="application/ld+json">
          {JSON.stringify(jsonLd)}
        </script>
      )}
    </Helmet>
  );
};

export default SeoHead;
